import { useEffect, useState, useCallback } from "react";
import { useAppDispatch } from "hooks";
import {
  FormLayout,
  Form,
  Button,
  Banner,
  TextField,
  Select,
} from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { fetchById, assetReturn } from "services/assets.service";
import Loading from "components/loading/Loading";
import { refreshAsset } from "containers/assets/assets.slice";
import { fetchAssetDetails } from "containers/asset-view-container/asset-container.slice";
import FileTemplateSelect from "components/file-template-select/file-template-select";
import { CustomField } from "types/CustomField";
import DatePickerx from "features/datepicker/date-picker";
import { fetchFileTemplateById } from "containers/files-template-form/files-template-form.service";

interface PostData {
  id?: string;
  templateId: string;
  date: string;
  // Custom fields can be plain strings or arrays (e.g. file fields store arrays of file IDs)
  customFields: { [key: string]: string | string[] };
  signersInfo?: {};
  address?: string[];
  fileTypeContext?: string;
  user?: string;
}

function AssetsReturnForm({
  id,
  onSuccess,
}: {
  id?: string;
  onSuccess?: () => void;
}) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams();
  const [assigned_to, setAssignedTo] = useState("");

  const [successMessage, setSuccessMessage] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [templateId, setTemplateId] = useState("");
  const [date, setDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [signType, setSignType] = useState("Click");

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [fileTypeContext, setFileTypeContext] = useState("");
  const [shouldFetchCustomFields, setShouldFetchCustomFields] = useState(false);

  // Text inputs/selects/date pickers expect string values, but CustomField.value can be string|string[]
  const getStringValue = (field: CustomField): string => {
    if (Array.isArray(field.value)) return field.value[0] || "";
    return field.value || "";
  };

  const getDatePart = (dateString: string) => {
    if (dateString.includes("T")) {
      return dateString.split("T")[0];
    }
    return dateString;
  };

  const fetchData = useCallback(
    async (id: string) => {
      setIsLoading(true);
      const { assigned_to, returnFileEntity: fileEntity } = await fetchById(
        companyId,
        id
      );
      setAssignedTo(assigned_to?.id);

      // file entity
      if (fileEntity) {
        const {
          date,
          templateId: templateId_,
          signType,
          filesTemplateCustomField,
          fileTypeContext,
        } = fileEntity;

        if (!templateId) {
          setTemplateId(templateId_?.id);
          setShouldFetchCustomFields(false);
        }
        if (signType) setSignType(signType);
        setDate(date);
        setFileTypeContext(fileTypeContext);
        const transformedFields = transformCustomFields(
          filesTemplateCustomField?.customFields
        );
        if (templateId_) {
          const fileTemplateEntity = await fetchFileTemplateById(
            companyId,
            templateId_.id
          );
          setCustomFields(
            fileTemplateEntity.customFields.map((field: CustomField) => ({
              ...field,
              value: transformedFields[field.label] || "",
            }))
          );
          setShouldFetchCustomFields(false);
        }
      }
      setIsLoading(false);
    },
    [companyId]
  );

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id, fetchData, companyId]);

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  const isFormValid = () => {
    let validationErrors: { [key: string]: string } = {};
    if (assigned_to || templateId) {
      if (!templateId) {
        validationErrors["templateId"] = t("template_is_required");
      }
      if (!date) {
        validationErrors["date"] = t("date_is_required");
      }

      // Check custom fields
      customFields.forEach((field) => {
        if (field.required && (!field.value || field.value?.length === 0)) {
          validationErrors[field.label] = t("field_is_required");
        }

        // Check minimum length for string and textarea fields
        if (
          field.minLength &&
          field.minLength > 0 &&
          field.value &&
          (field.type === "string" || field.type === "textarea")
        ) {
          if (field.value.length < field.minLength) {
            validationErrors[field.label] = t(
              "field_must_be_at_least_characters_label",
              { label: field.label, minLength: field.minLength }
            );
          }
        }
      });

      setErrors(validationErrors);
    }

    return Object.keys(validationErrors).length === 0; // Return false if errors exist
  };

  const transformCustomFields = (apiCustomFields: Record<string, any>) => {
    const transformedFields: Record<string, any> = {};
    if (apiCustomFields) {
      Object?.entries(apiCustomFields).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          // Extract only `id` values from array
          transformedFields[key] = value.map((entity) => entity?.id);
        } else if (
          value !== null &&
          typeof value === "object" &&
          !Array.isArray(value)
        ) {
          transformedFields[key] = value?.id;
        } else {
          transformedFields[key] = value;
        }
      });

      return transformedFields;
    }
    return {};
  };

  const fetchCustomFields = useCallback(
    async (templateId: string) => {
      try {
        const fileTemplateEntity = await fetchFileTemplateById(
          companyId,
          templateId
        );
        setCustomFields(
          fileTemplateEntity.customFields.map((field: CustomField) => ({
            ...field,
            value: "",
          }))
        );
      } catch (e) {
        console.error(e);
      }
    },
    [companyId]
  );

  useEffect(() => {
    if (templateId && shouldFetchCustomFields) {
      fetchCustomFields(templateId);
      setShouldFetchCustomFields(false); // Reset after fetching
    }
  }, [templateId, shouldFetchCustomFields, fetchCustomFields]);


  const handleCustomFieldChange = (index: number, value: string) => {
    const updatedFields = customFields.map((field, i) =>
      i === index ? { ...field, value } : field
    );
    setCustomFields(updatedFields);
  };

  if (!companyId) return null;

  const handleFormSubmit = async () => {
    if (!isFormValid()) {
      return;
    }
    const convertedDate = getDatePart(date);
    const formData: PostData = {
      id,
      date: convertedDate,
      templateId,
      customFields: {},
      signersInfo: {
        signType,
      },
      fileTypeContext,
    };
    customFields.forEach((field) => {
      if (field.type === "file") {
        // For file fields, ensure we send an array of file IDs
        const fileIds = Array.isArray(field.value) ? field.value : field.value ? [field.value] : [];
        formData.customFields[field.label] = fileIds;
      } else {
        // For non-file fields, send string value
        formData.customFields[field.label] = Array.isArray(field.value) ? "" : (field.value || "");
      }
    });

    try {
      setIsLoading(true);
      await assetReturn(companyId, id, formData);
      if (companyId && id) {
        // Refresh just this row in place (return changes status/assignment)
        // without resetting loaded pages or scroll position.
        dispatch(refreshAsset(companyId, id));
        dispatch(fetchAssetDetails({ companyId, id }));
      }
      setSuccessMessage("ok");
      setIsLoading(false);
      onSuccess?.();
      setTimeout(() => {
        closeModal();
      }, 1000);
    } catch (e) {
      setErrMsg("error");
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Form onSubmit={handleFormSubmit}>
      <FormLayout>
        <div className="form-warnings">
          {errMsg && (
            <div className="">
              <Banner tone="critical">{t(errMsg)}</Banner>
            </div>
          )}
          {successMessage && (
            <div className="">
              <Banner tone="info">{t(successMessage)}</Banner>
            </div>
          )}
        </div>

        {assigned_to && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <FileTemplateSelect
              onChange={(val) => {
                setTemplateId(val);
                setShouldFetchCustomFields(true); // Allow fetching on user change
              }}
              value={templateId}
              isRequired={true}
              error={errors["templateId"]}
              subject="assets"
            />
            {templateId && (
              <TextField
                label={t("file_context")}
                type="text"
                autoComplete="off"
                value={fileTypeContext}
                onChange={setFileTypeContext}
                labelHidden
                placeholder={t("enter_file_context")}
              />
            )}

            {templateId && (
              <DatePickerx
                disableDatesAfter={null}
                title={t("document_date")}
                isRequired={true}
                onChange={(value: any) =>
                  setDate(value.toISOString().split("T")[0])
                }
                selected={date}
              />
            )}
            {customFields.map((field, index) => (
              <div key={field.id}>
                {field.type === "string" && (
                  <TextField
                    label={field.name}
                    value={getStringValue(field)}
                    autoComplete="off"
                    onChange={(value: string) =>
                      handleCustomFieldChange(index, value)
                    }
                    requiredIndicator={field.required}
                    error={errors[field.label]}
                  />
                )}
                {field.type === "textarea" && (
                  <TextField
                    label={field.name}
                    value={getStringValue(field)}
                    multiline={4}
                    autoComplete="off"
                    onChange={(value: string) =>
                      handleCustomFieldChange(index, value)
                    }
                    requiredIndicator={field.required}
                    error={errors[field.label]}
                  />
                )}
                {field.type === "date" && (
                  <DatePickerx
                    disableDatesAfter={null}
                    title={field.name}
                    isRequired={field.required}
                    onChange={(value: any) =>
                      handleCustomFieldChange(
                        index,
                        value.toISOString().split("T")[0]
                      )
                    }
                    selected={getStringValue(field)}
                  />
                )}
                {field.type === "select" && (
                  <Select
                    label={field.name}
                    options={[
                      {
                        label: t(
                          "files_template_custom_type_select_select_an_option"
                        ),
                        value: "",
                      },
                      ...field.options.map((option) => ({
                        label: option,
                        value: option,
                      })),
                    ]}
                    value={getStringValue(field)}
                    onChange={(value: string) =>
                      handleCustomFieldChange(index, value)
                    }
                    requiredIndicator={field.required}
                    error={errors[field.label]}
                  />
                )}
              </div>
            ))}
          </div>
        )}
        <Button onClick={handleFormSubmit} loading={isLoading}>
          {t("save")}
        </Button>
      </FormLayout>
    </Form>
  );
}

export default AssetsReturnForm;
